import { AlertTriangle } from 'lucide-react'
import type { TemplateCheckReport } from '@/lib/api'
import {
  summariseTemplateCheck,
  templateFixHowTo,
  templateLossSentence,
} from '@/lib/template-check'

/**
 * The export template check in plain words (R3 T1): where the response goes,
 * what is kept, what isn't supported, and — for a template without a content
 * placeholder — what an export will replace (D4).
 */
export function TemplateCheckSummary({ report }: { report: TemplateCheckReport }) {
  const summary = summariseTemplateCheck(report)
  const loss = templateLossSentence(report)

  return (
    <div className="space-y-4">
      {loss && <TemplateLossNotice sentence={loss} howTo={templateFixHowTo(report)} />}

      <SummaryBlock label="Where your response goes">
        <p className="text-sm text-foreground">{summary.responseGoes}</p>
      </SummaryBlock>

      {summary.kept.length > 0 && (
        <SummaryBlock label="Kept from your template">
          <SummaryList items={summary.kept} />
        </SummaryBlock>
      )}

      {summary.unsupported.length + summary.notes.length > 0 && (
        <SummaryBlock label="Worth knowing">
          <SummaryList items={[...summary.unsupported, ...summary.notes]} />
        </SummaryBlock>
      )}
    </div>
  )
}

/** The D4 warning: what an export replaces, and how to keep it instead. */
export function TemplateLossNotice({ sentence, howTo }: { sentence: string; howTo: string }) {
  return (
    <div role="alert" className="flex items-start gap-2 rounded-lg bg-muted/40 p-3">
      <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--status-warning))]" />
      <div className="space-y-1">
        <p className="text-sm text-foreground">{sentence}</p>
        <p className="text-meta">{howTo}</p>
      </div>
    </div>
  )
}

function SummaryBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-meta">{label}</p>
      {children}
    </div>
  )
}

function SummaryList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2 text-sm text-foreground">
          <span aria-hidden className="mt-[0.45rem] h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}
